import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { Photo } from 'app/types/commonTypes.type';
import { emitTrackHdpPhotoGalleryOpenEvent } from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { getHaloPhotos } from 'app/shared/halo/utils/getHaloPhotos';
import { useDetailPanel } from '../../../ui/DetailPanel';
import { captureOpener, capturePreFocusScroll } from '../../../ui/FullscreenOverlay/FullscreenOverlayPortal';
import { MediaCenter } from '../MediaCenter';
import { MediaCenterPanel } from '../MediaCenter/MediaCenterPanel';
import type { MediaCenterTab } from '../MediaCenter/MediaCenterTabs';
import { PhotoGallery } from './PhotoGallery';

// Track whether the current interaction is pointer/touch vs keyboard.
// pointerdown fires before click for touch/mouse; keyboard Enter/Space
// fires click WITHOUT a preceding pointerdown.
let lastOpenWasPointer = false;
const handleGalleryPointerDown = () => {
  lastOpenWasPointer = true;
  if (window.matchMedia('(max-width: 767px)').matches) {
    capturePreFocusScroll();
  }
};
const captureOpenerWithModality = () => {
  captureOpener(document.activeElement as HTMLElement, !lastOpenWasPointer);
  lastOpenWasPointer = false;
};

export const PhotoGallerySection = () => {
  const { openPanel, isMobile } = useDetailPanel();
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTab, setMediaTab] = useState<MediaCenterTab>('photos');

  const listing = useSelector(
    (state: RootReduxState) =>
      state.currentListingDetails.currentListing || state.listings.listingGroups.previewListing,
  );

  const displayName = (listing as unknown as { displayName?: string })?.displayName || '';
  const photos = getHaloPhotos(listing);
  const tourUrls = (listing as unknown as { details?: { tourUrls?: Array<string> } })?.details?.tourUrls || [];
  const tourLabels = (listing as unknown as { details?: { tourLabels?: Array<string> } })?.details?.tourLabels || [];
  const has3dTour =
    (listing as unknown as { details?: { has3dTour?: boolean } })?.details?.has3dTour ||
    listing?.hasExternal3dTourUrls ||
    listing?.hasZillow3dTourUrls ||
    false;
  const listingVideos = listing?.listingVideos || [];
  const hasVideos = listingVideos.length > 0;
  const trusted = Boolean((listing as unknown as { trusted?: boolean })?.trusted);
  const geo = listing?.geo;
  const hasStreetView = geo?.lat != null && geo?.lon != null;

  const handleAllPhotosClick = () => {
    emitTrackHdpPhotoGalleryOpenEvent({ triggerLocation: 'home_details_halo' });
    if (isMobile) {
      captureOpenerWithModality();
      setMediaTab('photos');
      setMediaOpen(true);
    } else {
      openPanel('media-center', MediaCenterPanel, { config: 'photos' });
    }
  };

  const handle3dTourClick = () => {
    if (isMobile) {
      captureOpenerWithModality();
      setMediaTab('3dtour');
      setMediaOpen(true);
    } else {
      openPanel('media-center', MediaCenterPanel, { config: '3dtour' });
    }
  };

  const handleVideosClick = () => {
    if (isMobile) {
      captureOpenerWithModality();
      setMediaTab('videos');
      setMediaOpen(true);
    } else {
      openPanel('media-center', MediaCenterPanel, { config: 'videos' });
    }
  };

  const handleStreetViewClick = () => {
    if (isMobile) {
      captureOpenerWithModality();
      setMediaTab('streetview');
      setMediaOpen(true);
    } else {
      openPanel('media-center', MediaCenterPanel, { config: 'streetview' });
    }
  };

  return (
    <div onPointerDown={handleGalleryPointerDown}>
      <PhotoGallery
        photos={photos}
        displayName={displayName}
        onAllPhotosClick={handleAllPhotosClick}
        onGalleryTap={handleAllPhotosClick}
        has3dTour={has3dTour}
        hasVideos={hasVideos}
        hasStreetView={hasStreetView}
        on3dTourClick={handle3dTourClick}
        onVideosClick={handleVideosClick}
        onStreetViewClick={handleStreetViewClick}
        trusted={trusted}
      />

      {isMobile && (
        <MediaCenter
          open={mediaOpen}
          onOpenChange={setMediaOpen}
          photos={photos}
          tourUrls={tourUrls}
          tourLabels={tourLabels}
          has3dTour={has3dTour}
          hasVideos={hasVideos}
          hasStreetView={hasStreetView}
          videos={listingVideos}
          previewPhoto={(listing as { previewPhoto?: Photo })?.previewPhoto}
          initialTab={mediaTab}
          streetViewLat={geo?.lat}
          streetViewLon={geo?.lon}
        />
      )}
    </div>
  );
};
